import { useState, useEffect, useRef } from 'react'
import PillToggle from './PillToggle.jsx'

// Savings lookup by pass title keywords
const SAVINGS = {
  '10-class': { saving: 30, payg: 170 },
  '1-to-1': { saving: 15, payg: 225 },
  '2-to-1': { saving: 7.50, payg: 112.50 },
  'flexibility': { saving: 10, payg: 85 },
  'advanced-5': { saving: 10, payg: 110 },
  'advanced-10': { saving: 30, payg: 220 },
}

function getSaving(title) {
  const t = title.toLowerCase()
  const isAdvanced = t.includes('advanced')
  const is10Class = t.includes('10-class') || t.includes('10 class')

  if (isAdvanced) return is10Class ? SAVINGS['advanced-10'] : SAVINGS['advanced-5']
  if (is10Class) return SAVINGS['10-class']
  if (t.includes('1-to-1') || t.includes('1:1')) return SAVINGS['1-to-1']
  if (t.includes('2-to-1') || t.includes('2:1')) return SAVINGS['2-to-1']
  if (t.includes('flexibility') || t.includes('conditioning')) return SAVINGS['flexibility']
  return null
}

const CATEGORY_ORDER = ['Mixed Level Flexibility & Conditioning', 'Advanced Flexibility', 'Private 1:1', 'Private 2:1']

function getCategory(title) {
  const t = title.toLowerCase()
  if (t.includes('1-to-1') || t.includes('1:1')) return 'Private 1:1'
  if (t.includes('2-to-1') || t.includes('2:1')) return 'Private 2:1'
  if (t.includes('advanced')) return 'Advanced Flexibility'
  if (t.includes('flexibility') || t.includes('conditioning')) return 'Mixed Level Flexibility & Conditioning'
  return 'Other'
}

function useCountUp(target, duration = 800, start = false) {
  const [value, setValue] = useState(0)
  useEffect(() => {
    if (!start) return
    const startTime = performance.now()
    const tick = (now) => {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const ease = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(ease * target * 10) / 10)
      if (progress < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [start, target, duration])
  return value
}

function PassModal({ pass: p, onClose }) {
  const shortName = p.title
    .replace('Small Group Private Flexibility And/Or Aerial Conditioning Lessons: ', 'Small Group Flexibility & Conditioning · ')
    .replace('Private 1-To-1 Aerial (Straps/Silks/Rope) : ', 'Private 1:1 Aerial · ')
    .replace('Private 2-To-1 Aerial (Straps/Silks/Rope) : ', 'Private 2:1 Aerial · ')

  const saving = getSaving(p.title)

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', handler); document.body.style.overflow = '' }
  }, [onClose])

  return (
    <>
      <style>{`
        @keyframes modal-slide-up { from { opacity: 0; transform: translate(-50%, calc(-50% + 24px)); } to { opacity: 1; transform: translate(-50%, -50%); } }
        @keyframes backdrop-fade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, backdropFilter: 'blur(2px)', animation: 'backdrop-fade 0.25s ease forwards' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 201, width: 'min(520px,92vw)', background: 'var(--pitch)', color: 'var(--bone)', animation: 'modal-slide-up 0.35s cubic-bezier(0.22,1,0.36,1) forwards' }}>
        <div style={{ padding: '24px 28px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <div style={{ fontFamily: 'var(--f-b)', fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Class pass</div>
            <div style={{ fontFamily: 'var(--f-d)', fontSize: 'clamp(18px,3vw,24px)', letterSpacing: '0.05em', color: 'var(--bone)', lineHeight: 1.1 }}>{shortName}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--f-b)', fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.4)', flexShrink: 0, paddingTop: 4 }}>Close ✕</button>
        </div>
        <div style={{ padding: '20px 28px 28px' }}>
          <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
            {p.usageCredits && <div style={{ fontFamily: 'var(--f-b)', fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.65)' }}>{p.usageCredits} credits</div>}
            {p.validityDays && <div style={{ fontFamily: 'var(--f-b)', fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.65)' }}>{p.validityDays} days validity</div>}
            {saving && <div style={{ fontFamily: 'var(--f-b)', fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.65)' }}>vs £{saving.payg} pay as you go</div>}
          </div>
          {p.description && <p style={{ fontSize: 14, fontWeight: 300, color: 'rgba(255,255,255,0.55)', lineHeight: 1.85, marginBottom: 28 }}>{p.description}</p>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div>
              <div style={{ fontFamily: 'var(--f-d)', fontSize: 28, letterSpacing: '0.04em', color: 'var(--bone)' }}>{p.price}</div>
              {saving && <div style={{ fontFamily: 'var(--f-b)', fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>Save £{saving.saving} vs pay as you go</div>}
            </div>
            <a href={p.buyUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'var(--f-b)', fontSize: 14, fontWeight: 500, padding: '12px 24px', background: 'var(--bone)', color: 'var(--pitch)', textDecoration: 'none', borderRadius: 2 }}>Buy pass →</a>
          </div>
        </div>
      </div>
    </>
  )
}

function PassRow({ pass: p, index }) {
  const [hovered, setHovered] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [hoveredInfo, setHoveredInfo] = useState(false)
  const [visible, setVisible] = useState(false)
  const ref = useRef(null)
  const saving = getSaving(p.title)
  const countedSaving = useCountUp(saving?.saving || 0, 800, visible)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect() } }, { threshold: 0.2 })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const shortName = p.title
    .replace('Small Group Private Flexibility And/Or Aerial Conditioning Lessons: ', 'Small Group Flexibility & Conditioning · ')
    .replace('Private 1-To-1 Aerial (Straps/Silks/Rope) : ', 'Private 1:1 Aerial · ')
    .replace('Private 2-To-1 Aerial (Straps/Silks/Rope) : ', 'Private 2:1 Aerial · ')

  const handleGlow = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    e.currentTarget.style.setProperty('--glow-x', `${e.clientX - rect.left}px`)
    e.currentTarget.style.setProperty('--glow-y', `${e.clientY - rect.top}px`)
  }

  return (
    <>
      <div ref={ref} className="glow-card" onMouseMove={handleGlow} style={{
        display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center',
        gap: 24, padding: '16px 0', borderBottom: '1px solid rgba(255,255,255,0.06)',
        opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-20px)',
        transition: `opacity 0.5s ease ${index * 0.1}s, transform 0.5s cubic-bezier(0.22,1,0.36,1) ${index * 0.1}s`,
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ fontFamily: 'var(--f-d)', fontSize: 'clamp(16px,2vw,20px)', letterSpacing: '0.05em', color: 'var(--bone)', fontWeight: 400 }}>{shortName}</div>
            <button onClick={() => setShowModal(true)}
              onMouseEnter={() => setHoveredInfo(true)}
              onMouseLeave={() => setHoveredInfo(false)}
              style={{ background: 'none', border: `1px solid ${hoveredInfo ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.15)'}`, borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--f-m)', fontSize: 9, color: hoveredInfo ? 'var(--bone)' : 'rgba(255,255,255,0.3)', flexShrink: 0, transition: 'border-color 0.15s, color 0.15s' }}>i</button>
          </div>
          <div style={{ fontFamily: 'var(--f-b)', fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.6)', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            {p.usageCredits && <span>{p.usageCredits} credits</span>}
            {p.validityDays && <span>{p.validityDays} days</span>}
            {saving && visible && (
              <span style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'var(--f-b)', fontSize: 13, fontWeight: 400 }}>
                Save £{countedSaving % 1 === 0 ? countedSaving : countedSaving.toFixed(2)} vs pay as you go
              </span>
            )}
          </div>
        </div>
        <div style={{ fontFamily: 'var(--f-d)', fontSize: 22, letterSpacing: '0.04em', color: 'var(--bone)', whiteSpace: 'nowrap' }}>{p.price}</div>
        <a href={p.buyUrl} target="_blank" rel="noopener noreferrer"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{ fontFamily: 'var(--f-b)', fontSize: 13, fontWeight: 500, padding: '9px 18px', border: '1px solid rgba(255,255,255,0.25)', background: hovered ? 'var(--bone)' : 'transparent', color: hovered ? 'var(--pitch)' : 'var(--bone)', cursor: 'pointer', borderRadius: 2, whiteSpace: 'nowrap', textDecoration: 'none', transition: 'background 0.25s, color 0.25s' }}>
          Buy pass →
        </a>
      </div>
      {showModal && <PassModal pass={p} onClose={() => setShowModal(false)} />}
    </>
  )
}

export default function ClassPasses({ passes, loading }) {
  const categoriesPresent = passes ? CATEGORY_ORDER.filter(cat => passes.some(p => getCategory(p.title) === cat)) : []
  const [activeCategory, setActiveCategory] = useState(null)

  useEffect(() => {
    if (!activeCategory && categoriesPresent.length > 0) setActiveCategory(categoriesPresent[0])
  }, [categoriesPresent.join(',')])

  if (loading) return null
  if (!passes || passes.length === 0) return null

  const filteredPasses = passes.filter(p => getCategory(p.title) === activeCategory)

  return (
    <>
      <style>{`
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes line-draw {
          from { width: 0; }
          to { width: 100%; }
        }
        .passes-section {
          background: linear-gradient(135deg, #0a0a0a 0%, #111 25%, #0d0d0d 50%, #0f0b08 75%, #0a0a0a 100%);
          background-size: 400% 400%;
          animation: gradient-shift 8s ease infinite;
          position: relative;
          overflow: hidden;
        }
        .passes-section::before {
          content: '';
          position: absolute;
          top: 0; left: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          animation: line-draw 1.2s cubic-bezier(0.22,1,0.36,1) 0.2s both;
        }
      `}</style>
      <section id="passes" className="passes-section" style={{ padding: 'clamp(64px,10vw,120px) clamp(24px,5vw,56px)' }}>
        <div style={{ fontFamily: 'var(--f-m)', fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', marginBottom: 14, position: 'relative', zIndex: 1 }}>
          Save when you commit
        </div>
        <h2 style={{ fontFamily: 'var(--f-d)', fontSize: 'clamp(32px,5vw,52px)', letterSpacing: '0.04em', lineHeight: 1, fontWeight: 400, color: 'var(--bone)', marginBottom: 28, position: 'relative', zIndex: 1 }}>
          Class passes
        </h2>

        {categoriesPresent.length > 1 && (
          <div style={{ marginBottom: 32, position: 'relative', zIndex: 1 }}>
            <PillToggle
              options={categoriesPresent.map(cat => ({ key: cat, label: cat }))}
              active={activeCategory}
              onChange={setActiveCategory}
              onDark={true}
            />
          </div>
        )}

        <div style={{ position: 'relative', zIndex: 1 }}>
          {filteredPasses.map((p, i) => (
            <PassRow key={p.id} pass={p} index={i} />
          ))}
        </div>
      </section>
    </>
  )
}
